import { Node, mergeAttributes } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    videoEmbed: {
      setVideoEmbed: (url: string) => ReturnType;
    };
  }
}

const YOUTUBE_SHORTS_REGEX = /youtube\.com\/shorts\/([^/?]+)/i;
const YOUTUBE_WATCH_REGEX = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/i;
const VIMEO_REGEX = /vimeo\.com\/(?:video\/)?(\d+)/i;
// Same pattern used for exercise videos (src/utils/videoEmbedUtils.ts), kept in sync for consistency.
const DRIVE_REGEX = /drive\.google\.com\/file\/d\/([^/]+)/i;

// Only web addresses go in the iframe src: a javascript: or data: src runs script in the page that shows the article (#118).
// The browser URL parser reads the scheme, so upper case, leading spaces and tabs inside it are caught too.
const isWebUrl = (value: string, base?: string): boolean => {
  try {
    return ['http:', 'https:'].includes(new URL(value, base).protocol);
  } catch {
    return false;
  }
};

export const toEmbedUrl = (rawUrl: string): string | null => {
  const url = rawUrl.trim();
  if (!url) return null;

  const shortsMatch = url.match(YOUTUBE_SHORTS_REGEX);
  if (shortsMatch) {
    return `https://www.youtube.com/embed/${shortsMatch[1]}`;
  }

  const youtubeMatch = url.match(YOUTUBE_WATCH_REGEX);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }

  const vimeoMatch = url.match(VIMEO_REGEX);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  const driveMatch = url.match(DRIVE_REGEX);
  if (driveMatch) {
    return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  }

  return isWebUrl(url) ? new URL(url).href : null;
};

export const VideoEmbed = Node.create({
  name: 'videoEmbed',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-video-embed]',
        getAttrs: (element) => {
          const src = (element as HTMLElement).querySelector('iframe')?.getAttribute('src') ?? null;
          // A relative saved src is read against a fixed web base, so only another scheme is dropped, on the web and on native.
          return { src: src !== null && isWebUrl(src, 'https://relative.invalid/') ? src : null };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-video-embed': '', class: 'rich-text-video-embed' }),
      ['iframe', { src: HTMLAttributes.src, frameborder: '0', allow: 'fullscreen', allowfullscreen: 'true' }],
    ];
  },

  addCommands() {
    return {
      setVideoEmbed:
        (url) =>
        ({ commands }) => {
          const embedUrl = toEmbedUrl(url);
          if (!embedUrl) return false;
          return commands.insertContent({ type: this.name, attrs: { src: embedUrl } });
        },
    };
  },
});
